import React from "react";
import { HardHat, Briefcase, Stethoscope, UserCog } from "lucide-react";
import ChapterShell from "@/tutorial/ChapterShell";
import {
  Section,
  Quote,
  FieldExplainer,
  RevealList,
  TipBox,
  Quiz,
} from "@/tutorial/components/TutorialComponents";

const TEMPLATES = [
  {
    icon: HardHat,
    name: "Ouvrier SMIG",
    blurb:
      "Contribuable au salaire minimum, charges proportionnelles, peu d'impôt direct mais TVA forte.",
  },
  {
    icon: Briefcase,
    name: "Fonctionnaire",
    blurb:
      "Revenu stable, cotisations sociales élevées, imposition médiane.",
  },
  {
    icon: Stethoscope,
    name: "Cadre supérieur",
    blurb:
      "Tranche haute de l'IR, cotisations plafonnées, exposition fiscale maximale.",
  },
  {
    icon: UserCog,
    name: "Personnalisé",
    blurb:
      "Paramètres entièrement libres pour modéliser un cas particulier.",
  },
];

const CALC_ROWS = [
  { label: "Salaire journalier", formula: "500 ÷ 30", value: "16,67 UM/jour" },
  { label: "Impôt sur le revenu annuel", formula: "", value: "600 UM" },
  { label: "Cotisations sociales", formula: "", value: "480 UM" },
  { label: "TVA annuelle estimée", formula: "", value: "7 200 UM" },
  { label: "Autres prélèvements", formula: "", value: "120 UM" },
  { label: "TOTAL prélevé / an", formula: "600 + 480 + 7 200 + 120", value: "8 400 UM" },
  { label: "Jours de vie sacrifiés / an", formula: "8 400 ÷ 16,67", value: "504 jours" },
  { label: "Mois de vie / an", formula: "504 ÷ 30", value: "16,8 mois" },
];

export default function Chapter3() {
  return (
    <ChapterShell id={3}>
      <Section
        id="section-3-1"
        eyebrow="3.1"
        title="Pourquoi un profil contribuable ?"
      >
        <Quote author="Théorie vitaliste · ELY Mustapha">
          Deux citoyens d'un même pays ne paient jamais le même nombre de jours
          de vie. Le même impôt n'est pas la même peine.
        </Quote>
        <p>
          Le profil contribuable fixe la <strong>référence vitaliste</strong>{" "}
          de l'analyse : salaire, cotisations, taux d'imposition, consommation
          annuelle. C'est à travers ce profil que l'application convertira les
          prélèvements en jours de vie humaine.
        </p>
      </Section>

      <Section id="section-3-2" eyebrow="3.2" title="Choisir un profil prédéfini">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {TEMPLATES.map((t) => {
            const Icon = t.icon;
            return (
              <div
                key={t.name}
                className="rounded-xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-brand-gold/60 hover:shadow-md"
                data-testid={`chapter-3-template-${t.name}`}
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-blue text-white">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="mt-3 font-semibold">{t.name}</div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {t.blurb}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section
        id="section-3-3"
        eyebrow="3.3"
        title="Comprendre chaque paramètre"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FieldExplainer
            label="Salaire mensuel brut"
            formula="Variable d'entrée"
            example="500 UM/mois"
            hint="Rémunération avant cotisations. Base de tous les calculs."
          />
          <FieldExplainer
            label="Salaire journalier"
            formula="Salaire_mensuel / 30"
            example="500 / 30 = 16,67 UM/jour"
            hint="Référence pédagogique : une journée de vie = ce que vaut un jour de travail."
          />
          <FieldExplainer
            label="Taux d'IR effectif"
            formula="IR annuel / revenu annuel"
            example="600 / 6 000 = 10 %"
            hint="Inclut abattements, quotient familial, tranches."
          />
          <FieldExplainer
            label="Cotisations sociales"
            formula="% plafonné sur le salaire"
            example="8 % → 480 UM/an"
            hint="Santé, retraite, chômage selon le pays."
          />
          <FieldExplainer
            label="TVA annuelle estimée"
            formula="Consommation × taux moyen"
            example="40 000 × 18 % = 7 200 UM"
            hint="Basée sur la propension à consommer du profil."
          />
          <FieldExplainer
            label="Autres prélèvements"
            formula="Taxes spécifiques + accises"
            example="120 UM/an (carburant, alcool…)"
            hint="Ajustement fin selon la nature du contribuable."
          />
        </div>
      </Section>

      <Section
        id="section-3-4"
        eyebrow="3.4"
        title="Exemple complet de calcul pas à pas"
      >
        <p>
          Ouvrier SMIG en Mauritanie, salaire mensuel de 500 UM. Les chiffres
          ci-dessous apparaissent séquentiellement : observez comment la
          monnaie se transforme en jours de vie.
        </p>

        <div className="overflow-hidden rounded-lg border border-border">
          <RevealList stagger={220}>
            {CALC_ROWS.map((r, i) => (
              <div
                key={i}
                className="grid grid-cols-12 items-center gap-2 border-t border-border px-4 py-2.5 text-sm first:border-t-0 odd:bg-muted/30"
              >
                <span className="col-span-5 font-medium text-foreground">
                  {r.label}
                </span>
                <span className="col-span-4 font-mono text-xs text-muted-foreground">
                  {r.formula || "—"}
                </span>
                <span className="col-span-3 text-right font-mono font-semibold text-brand-blue dark:text-brand-gold">
                  {r.value}
                </span>
              </div>
            ))}
          </RevealList>
        </div>

        <div className="rounded-lg border-2 border-red-600/50 bg-red-600/10 p-6 text-center">
          <div className="text-[11px] font-bold uppercase tracking-widest text-red-700 dark:text-red-300">
            Conclusion vitaliste
          </div>
          <div className="mt-2 font-judicial text-2xl font-black leading-tight text-red-700 dark:text-red-300 sm:text-3xl">
            55,2 ans de vie au service de l'État
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            504 jours × carrière de 40 ans / 365 ≈ 55,2 années-vie cumulées.
          </div>
        </div>

        <TipBox title="Compare pour révéler les inégalités">
          Créez trois profils simultanément (ouvrier / fonctionnaire / cadre)
          pour révéler les écarts vitalistes entre catégories sociales. Un même
          État ne consomme jamais la même quantité de vie selon qui paie.
        </TipBox>
      </Section>

      <Section id="section-3-5" eyebrow="Validation" title="Quiz du chapitre">
        <Quiz
          question="Quelle formule donne le salaire journalier de référence ?"
          options={[
            "Salaire annuel ÷ 365",
            "Salaire mensuel ÷ 30",
            "Salaire mensuel ÷ 22",
          ]}
          correct={1}
          explanation="La théorie vitaliste utilise 30 jours (et non 22 jours ouvrés) pour refléter la dimension existentielle — la vie ne s'arrête pas le week-end."
        />
      </Section>
    </ChapterShell>
  );
}
